import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Phone, Mail, CheckCircle2, XCircle, Trash2, X, Clock, Calendar, Pencil,
  Send, FileText, AlertTriangle, Flame, Radio, WifiOff, RefreshCw, Plus,
  Activity, ChevronDown, ChevronRight, MessageCircle, Inbox, CheckCheck,
} from "lucide-react";
import { api, errMsg, getAccessToken } from "../../lib/api";
import { WsPageTitle } from "./WorkspaceLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Field } from "../../components/admin/Fields";
import AppointmentRequestModal from "../../components/admin/AppointmentRequestModal";

/**
 * Talep Takibi — Kanban main screen (replaces the old "Gelen Kutusu").
 *
 * Columns (status-colour coded as the user asked):
 *   🔴  Acil         — call_me (not yet called)  OR  stale > 48h awaiting_approval
 *   🔵  Yeni         — awaiting_approval + pending_response + draft (recent)
 *   🟡  Devam Eden   — call_me (already called, waiting for next action)
 *   🟢  Tamamlandı   — approved (last 20)
 *
 * Rejected items hide behind a collapsible drawer.
 *
 * Live-updates: subscribes to the same WebSocket as before so brand-new requests
 * fly into their column with a yellow flash. Messages hitting the WS are
 * dispatched as a `window` event ("inbox:new-message") so the floating chat
 * bubble can pick them up without duplicating the socket.
 */

const STATUS_META = {
  draft:              { label: "Taslak",            cls: "bg-stone-100 text-stone-600 border-stone-300" },
  pending_response:   { label: "Yanıt bekleniyor",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
  awaiting_approval:  { label: "Onay bekleniyor",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
  approved:           { label: "Onaylandı",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:           { label: "Reddedildi",        cls: "bg-rose-50 text-rose-700 border-rose-200" },
  call_me:            { label: "Arama Bekliyor",    cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return "az önce";
  if (diff < 60) return `${Math.round(diff)} dk önce`;
  if (diff < 1440) return `${Math.round(diff / 60)} sa önce`;
  return `${Math.round(diff / 1440)} g önce`;
};
const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const z = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
};

function wsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const u = new URL(base, window.location.href);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws/admin/inbox";
  const token = getAccessToken();
  if (token) u.searchParams.set("token", token);
  return u.toString();
}

// --- Modals (copied verbatim from AppointmentsAdmin to keep behaviour) ----
function ApproveModal({ req, onClose, onDone }) {
  const [slot, setSlot] = useState(req.proposed_slots?.[0] ? toLocal(req.proposed_slots[0]) : "");
  const [location, setLocation] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [override, setOverride] = useState(false);

  useEffect(() => {
    if (!slot) { setConflicts([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const iso = new Date(slot).toISOString();
        const r = await api.get(`/admin/appointments/${req.id}/conflicts`, { params: { slot: iso, duration: req.duration_minutes || 30 } });
        if (alive) setConflicts(r.data?.conflicts || []);
      } catch { if (alive) setConflicts([]); }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [slot, req.id, req.duration_minutes]);

  const submit = async () => {
    if (!slot) return toast.error("Tarih seçin");
    if (conflicts.length > 0 && !override) return toast.error("Çakışma var — devam etmek için onaylayın");
    setBusy(true);
    try {
      await api.post(`/admin/appointments/${req.id}/approve`, { slot: new Date(slot).toISOString(), location, message });
      toast.success("Randevu onaylandı");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="approve-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
          <h2 className="font-serif text-xl font-bold">Randevuyu onayla</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="text-xs text-stone-500 mb-1">Önerilen tarihler</div>
            <div className="space-y-1.5">
              {(req.proposed_slots || []).map((s, i) => (
                <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="proposed" checked={toLocal(s) === slot} onChange={() => setSlot(toLocal(s))} />
                  <span>{fmt(s)}</span>
                </label>
              ))}
            </div>
          </div>
          <Field label="Randevu tarih/saati">
            <Input type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="approve-slot" />
          </Field>
          {conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm">
              <div className="font-semibold flex items-center gap-1.5 mb-2"><AlertTriangle size={14} /> Bu saatte başka etkinlik var</div>
              <ul className="space-y-1 text-xs">
                {conflicts.map((c) => (
                  <li key={c.id} className="flex items-center gap-2">
                    <Calendar size={11} className="flex-shrink-0" />
                    <span className="truncate"><strong>{c.title}</strong> · {fmt(c.start_at)}{c.location ? " · " + c.location : ""}</span>
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-2 text-xs mt-3 cursor-pointer">
                <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                <span>Yine de onayla (çift rezervasyon olacak)</span>
              </label>
            </div>
          )}
          <Field label="Yer (opsiyonel)"><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Lefkoşa / Online" /></Field>
          <Field label="Kişisel mesaj (opsiyonel)"><Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} /></Field>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>Vazgeç</Button>
            <Button onClick={submit} disabled={busy || (conflicts.length > 0 && !override)} data-testid="approve-submit">Onayla & E-posta Gönder</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RejectModal({ req, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/appointments/${req.id}/reject`, { reason });
      toast.success("Talep reddedildi"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-serif text-xl font-bold">Talebi reddet</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Açıklama (opsiyonel, karşı tarafa iletilir)">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          </Field>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>Vazgeç</Button>
            <Button variant="destructive" onClick={submit} disabled={busy}>Reddet</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Kanban Card ---------------------------------------------------------
function RequestCard({ req, columnTint, isFresh, onApprove, onReject, onDelete, onCopy, onEditDraft, onSendDraft, onMarkCalled }) {
  const meta = STATUS_META[req.status] || { label: req.status, cls: "bg-stone-50 text-stone-600 border-stone-200" };
  const emails = req.emails?.length ? req.emails : (req.email ? [req.email] : []);
  const phones = req.phones?.length ? req.phones : (req.phone ? [req.phone] : []);
  const link = `${window.location.origin}/api/public/appointment/${req.token || ""}`;
  const urgent = req.status === "call_me" && !req.called_at;

  return (
    <div
      className={`relative bg-white rounded-xl border-l-4 shadow-sm hover:shadow-md p-3.5 transition group ${columnTint}
        ${isFresh ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
      data-testid={`request-card-${req.id}`}
    >
      {urgent && (
        <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1 shadow-md">
          <Flame size={10} /> ACİL
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 truncate text-sm">
            {req.name || <span className="text-stone-400 italic">İsimsiz taslak</span>}
          </div>
          {req.title && <div className="text-xs text-amber-700 font-medium truncate mt-0.5">{req.title}</div>}
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.cls}`}>{meta.label}</span>
      </div>

      <div className="text-[11px] text-stone-500 mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
        {emails.slice(0, 1).map((em, i) => (
          <span key={i} className="inline-flex items-center gap-1 truncate max-w-full"><Mail size={10} />{em}</span>
        ))}
        {phones.slice(0, 1).map((ph, i) => (
          <span key={i} className="inline-flex items-center gap-1"><Phone size={10} />{ph}</span>
        ))}
        <span className="inline-flex items-center gap-1"><Clock size={10} />{relTime(req.created_at)}</span>
      </div>

      {req.reason && (
        <p className="mt-2 text-xs text-stone-700 line-clamp-2 bg-stone-50 border border-stone-200 rounded px-2 py-1.5"
           dangerouslySetInnerHTML={{ __html: req.reason }} />
      )}

      {req.status === "awaiting_approval" && req.proposed_slots?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {req.proposed_slots.slice(0, 3).map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-2 py-0.5 text-[10px]">
              <Calendar size={9} /> {fmt(s)}
            </span>
          ))}
        </div>
      )}

      {req.status === "approved" && req.selected_slot && (
        <div className="mt-2 inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-2 py-0.5 text-[10px]">
          <CheckCircle2 size={10} /> {fmt(req.selected_slot)}
        </div>
      )}

      {req.status === "call_me" && req.call_me_phone && (
        <div className="mt-2 inline-flex items-center gap-1 bg-violet-50 border border-violet-200 text-violet-700 rounded-full px-2 py-0.5 text-[10px]">
          <Phone size={10} /> {req.call_me_phone}
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex items-center flex-wrap gap-1.5">
        {req.status === "draft" && (
          <>
            <button onClick={() => onSendDraft(req)} className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700" data-testid={`send-draft-${req.id}`}>
              <Send size={11} /> Gönder
            </button>
            <button onClick={() => onEditDraft(req)} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-stone-50">
              <Pencil size={11} /> Düzenle
            </button>
          </>
        )}
        {req.status === "awaiting_approval" && (
          <>
            <button onClick={() => onApprove(req)} className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`approve-${req.id}`}>
              <CheckCircle2 size={11} /> Onayla
            </button>
            <button onClick={() => onReject(req)} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-rose-50 text-rose-600 border-rose-200" data-testid={`reject-${req.id}`}>
              <XCircle size={11} /> Reddet
            </button>
          </>
        )}
        {req.status === "pending_response" && (
          <button onClick={() => onCopy(link)} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-stone-50">
            🔗 Bağlantı
          </button>
        )}
        {req.status === "call_me" && !req.called_at && req.call_me_phone && (
          <>
            <a href={`tel:${req.call_me_phone}`} className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-600 text-white hover:bg-red-700">
              <Phone size={11} /> Hemen Ara
            </a>
            <button onClick={() => onMarkCalled(req)} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-stone-50">
              <CheckCircle2 size={11} /> Arandı
            </button>
          </>
        )}
        {req.status === "call_me" && req.called_at && (
          <button onClick={() => onApprove(req)} className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
            <Calendar size={11} /> Takvime Ekle
          </button>
        )}
        <button onClick={() => onDelete(req)} className="ml-auto text-stone-300 hover:text-red-600" title="Sil" data-testid={`del-${req.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function Column({ title, color, icon: Icon, items, children, badge }) {
  return (
    <div className={`sm:flex-1 min-w-full sm:min-w-[280px] rounded-2xl border-2 ${color.border} ${color.bg} flex flex-col`} data-testid={`kanban-col-${title.toLowerCase()}`}>
      <div className={`px-4 py-3 flex items-center gap-2 border-b-2 ${color.border}`}>
        <div className={`w-7 h-7 rounded-lg ${color.icon} flex items-center justify-center text-white shadow`}>
          <Icon size={14} />
        </div>
        <h3 className={`text-sm font-bold ${color.text}`}>{title}</h3>
        <span className={`ml-auto text-[11px] font-bold ${color.text} ${color.badge} rounded-full px-2 py-0.5`}>
          {badge ?? items.length}
        </span>
      </div>
      <div className="p-3 space-y-2 overflow-y-auto flex-1 max-h-[380px] sm:max-h-[calc(100vh-280px)]">
        {items.length === 0 ? (
          <div className="text-center text-xs text-stone-400 italic py-8">— boş —</div>
        ) : children}
      </div>
    </div>
  );
}

/** Inline messages panel pinned next to the Kanban on xl+ screens. Reuses the
 * same WS event broadcast (`inbox:ws-event`) so new messages flash in without
 * opening a second WebSocket. */
function MessagesSidePanel() {
  const qc = useQueryClient();
  const [flashId, setFlashId] = useState(null);
  const { data = [], refetch, isFetching } = useQuery({
    queryKey: ["admin", "messages"],
    queryFn: () => api.get("/admin/messages").then((r) => r.data),
    refetchInterval: 30000,
  });
  const live = data.filter((m) => !m.deleted);
  const unread = live.filter((m) => !m.read);
  useEffect(() => {
    const h = (e) => {
      const msg = e.detail;
      if (!msg || msg.type !== "new" || msg.kind !== "message") return;
      setFlashId(msg.item?.id);
      qc.invalidateQueries({ queryKey: ["admin", "messages"] });
      setTimeout(() => setFlashId(null), 4000);
    };
    window.addEventListener("inbox:ws-event", h);
    return () => window.removeEventListener("inbox:ws-event", h);
  }, [qc]);
  const mark = async (m, read) => {
    try { await api.patch(`/admin/messages/${m.id}`, { read }); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const trashIt = async (m) => {
    if (!window.confirm("Mesaj çöpe atılsın mı?")) return;
    try { await api.delete(`/admin/messages/${m.id}`); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const fmtR = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 60000;
    if (diff < 1) return "az önce";
    if (diff < 60) return `${Math.round(diff)} dk önce`;
    if (diff < 1440) return `${Math.round(diff / 60)} sa önce`;
    return `${Math.round(diff / 1440)} g önce`;
  };
  return (
    <div className="rounded-2xl border-2 border-indigo-200 bg-white flex flex-col h-full max-h-[calc(100vh-230px)] shadow-sm overflow-hidden" data-testid="messages-panel-wrapper">
      <div className="px-4 py-3 flex items-center gap-2 text-white"
           style={{ background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 55%,#c026d3 100%)" }}>
        <MessageCircle size={16} />
        <div className="flex-1">
          <div className="text-sm font-bold">Mesajlar</div>
          <div className="text-[11px] opacity-80">
            {unread.length > 0 ? `${unread.length} okunmamış` : "Tümü okundu"}
          </div>
        </div>
        <button onClick={() => refetch()} className="p-1 rounded hover:bg-white/20" title="Yenile">
          <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-gradient-to-b from-slate-50 to-white">
        {live.length === 0 && (
          <div className="text-center text-sm text-stone-400 italic p-8">
            <MessageCircle className="mx-auto mb-2 opacity-30" size={24} />
            Henüz mesaj yok.
          </div>
        )}
        {live.slice(0, 50).map((m) => (
          <div key={m.id}
               className={`rounded-lg p-2 border transition
                 ${m.read ? "bg-white border-stone-200" : "bg-indigo-50/60 border-indigo-200 shadow-sm"}
                 ${flashId === m.id ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
               data-testid={`panel-msg-${m.id}`}>
            <div className="flex items-start gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-[11px] shrink-0 ${m.read ? "bg-stone-400" : "bg-gradient-to-br from-indigo-500 to-violet-600"}`}>
                {(m.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className={`text-xs truncate flex-1 ${m.read ? "text-stone-700" : "font-semibold text-slate-900"}`}>{m.name || "(isimsiz)"}</p>
                  <span className="text-[10px] text-stone-400 shrink-0">{fmtR(m.created_at)}</span>
                </div>
                <div className="text-[11px] text-stone-500 truncate">{m.email || m.phone}</div>
                {m.subject && <div className="text-[11px] font-medium text-stone-700 mt-0.5 truncate">{m.subject}</div>}
                <p className="text-xs text-stone-600 line-clamp-2 mt-1">{m.message || m.area || "—"}</p>
                <div className="flex items-center gap-1 mt-1.5">
                  <a href={`mailto:${m.email}`} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                    <Mail size={9} /> Yanıtla
                  </a>
                  {!m.read ? (
                    <button onClick={() => mark(m, true)} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border hover:bg-stone-50" data-testid={`panel-msg-read-${m.id}`}>
                      <CheckCheck size={9} /> Okundu
                    </button>
                  ) : (
                    <button onClick={() => mark(m, false)} className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border hover:bg-stone-50" data-testid={`panel-msg-unread-${m.id}`}>
                      Okunmadı
                    </button>
                  )}
                  <button onClick={() => trashIt(m)} className="text-[10px] ml-auto p-0.5 rounded text-stone-400 hover:text-red-600" data-testid={`panel-msg-del-${m.id}`}>
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RequestsKanban() {
  const qc = useQueryClient();
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [freshHits, setFreshHits] = useState([]);
  const [mobileTab, setMobileTab] = useState("kanban"); // 'kanban' | 'messages'
  const wsRef = useRef(null);
  const pingTimer = useRef(null);
  const reconnectTimer = useRef(null);

  const { data: items = [], isFetching, refetch } = useQuery({
    queryKey: ["admin", "appointments", "all"],
    queryFn: () => api.get("/admin/appointments?status=").then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin", "appointments"] });
    qc.invalidateQueries({ queryKey: ["inbox"] });
  };
  const remove = async (req) => {
    if (!window.confirm(`"${req.name || req.title || "Bu talep"}" kalıcı olarak silinecek. Emin misiniz?`)) return;
    try { await api.delete(`/admin/appointments/${req.id}`); refresh(); toast.success("Silindi"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copy = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Bağlantı kopyalandı"); }
    catch { toast.error("Kopyalanamadı"); }
  };
  const editDraft = (req) => { setEditingDraft(req); setModalOpen(true); };
  const sendDraft = async (req) => {
    try { await api.post(`/admin/appointments/${req.id}/send`); toast.success("Taslak gönderildi"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const markCalled = async (req) => {
    try { await api.post(`/admin/appointments/${req.id}/mark-called`); toast.success("Arandı olarak işaretlendi"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  // Kanban bucketing
  const buckets = useMemo(() => {
    const b = { urgent: [], newReqs: [], inProgress: [], done: [], rejected: [] };
    for (const r of items) {
      if (r.status === "rejected") { b.rejected.push(r); continue; }
      if (r.status === "approved") { b.done.push(r); continue; }
      if (r.status === "call_me") {
        if (r.called_at) b.inProgress.push(r);
        else b.urgent.push(r);
        continue;
      }
      // awaiting_approval, pending_response, draft all go to Yeni
      b.newReqs.push(r);
    }
    // Stale awaiting_approval (>48h) should bubble to urgent too.
    const stale = b.newReqs.filter((r) => {
      if (r.status !== "awaiting_approval") return false;
      const age = (Date.now() - new Date(r.created_at).getTime()) / 3600000;
      return age > 48;
    });
    b.urgent.push(...stale);
    b.newReqs = b.newReqs.filter((r) => !stale.includes(r));
    return b;
  }, [items]);

  // WebSocket: refetch on new appointment + broadcast message events to the bubble.
  useEffect(() => {
    let closed = false;
    const connect = () => {
      if (closed) return;
      try {
        const ws = new WebSocket(wsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          setWsConnected(true);
          pingTimer.current = setInterval(() => { try { ws.send("ping"); } catch {} }, 25000);
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type !== "new" || !msg.item) return;
            if (msg.kind === "appointment") {
              setFreshHits((h) => [msg.item.id, ...h].slice(0, 10));
              qc.invalidateQueries({ queryKey: ["admin", "appointments"] });
              qc.invalidateQueries({ queryKey: ["inbox"] });
              // Clear fresh flag after 5s
              setTimeout(() => setFreshHits((h) => h.filter((id) => id !== msg.item.id)), 5000);
            }
            // Always re-broadcast so other components (chat bubble, bell) can react.
            window.dispatchEvent(new CustomEvent("inbox:ws-event", { detail: msg }));
          } catch {}
        };
        ws.onclose = () => {
          setWsConnected(false);
          if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
          if (closed) return;
          reconnectTimer.current = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch { reconnectTimer.current = setTimeout(connect, 5000); }
    };
    connect();
    return () => {
      closed = true;
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [qc]);

  const COLS = {
    urgent:     { border: "border-red-200",    bg: "bg-red-50/40",     text: "text-red-800",    icon: "bg-gradient-to-br from-red-500 to-rose-600",      badge: "bg-red-100",    tint: "border-red-400" },
    newReqs:    { border: "border-blue-200",   bg: "bg-blue-50/40",    text: "text-blue-800",   icon: "bg-gradient-to-br from-blue-500 to-indigo-600",   badge: "bg-blue-100",   tint: "border-blue-400" },
    inProgress: { border: "border-amber-200",  bg: "bg-amber-50/40",   text: "text-amber-800",  icon: "bg-gradient-to-br from-amber-500 to-orange-600",  badge: "bg-amber-100",  tint: "border-amber-400" },
    done:       { border: "border-emerald-200",bg: "bg-emerald-50/40", text: "text-emerald-800",icon: "bg-gradient-to-br from-emerald-500 to-green-600", badge: "bg-emerald-100",tint: "border-emerald-400" },
  };

  const handlers = {
    onApprove: setApproving, onReject: setRejecting, onDelete: remove, onCopy: copy,
    onEditDraft: editDraft, onSendDraft: sendDraft, onMarkCalled: markCalled,
  };

  return (
    <div data-testid="requests-kanban">
      <WsPageTitle
        icon={Activity}
        title="Aksiyon Takibi"
        sub="Randevu talepleri, geri arama istekleri, mesajlar — hepsi tek ekranda canlı akar."
        action={
          <div className="flex items-center gap-2">
            {wsConnected ? (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1 text-xs font-semibold" data-testid="kanban-ws-live">
                <Radio size={11} className="animate-pulse" /> Canlı
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2.5 py-1 text-xs">
                <WifiOff size={11} /> Bağlanıyor
              </span>
            )}
            <button onClick={() => refetch()} className="p-1.5 rounded-full border bg-white hover:bg-stone-50" title="Yenile">
              <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
            </button>
            <a href="/admin/ayarlar?tab=iletisim" className="text-xs text-stone-500 hover:text-amber-700 inline-flex items-center gap-1">
              <Pencil size={12} /> Şablon
            </a>
            <Button onClick={() => { setEditingDraft(null); setModalOpen(true); }} data-testid="kanban-new-request">
              <Plus size={14} className="mr-1" /> Yeni Talep
            </Button>
          </div>
        }
      />

      {/* Mobile/tablet tab switcher (xl+ shows both inline, so hide there) */}
      <div className="xl:hidden flex items-center gap-1 bg-white border border-stone-200 rounded-lg p-1 mb-3 w-full max-w-sm" data-testid="kanban-mobile-tabs">
        <button
          onClick={() => setMobileTab("kanban")}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded ${mobileTab === "kanban" ? "bg-indigo-600 text-white shadow" : "text-stone-600 hover:bg-stone-50"}`}
          data-testid="kanban-tab-requests"
        >
          <Activity size={12} /> Talepler
        </button>
        <button
          onClick={() => setMobileTab("messages")}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded ${mobileTab === "messages" ? "bg-indigo-600 text-white shadow" : "text-stone-600 hover:bg-stone-50"}`}
          data-testid="kanban-tab-messages"
        >
          <MessageCircle size={12} /> Mesajlar
        </button>
      </div>

      {/* Kanban + inline messages panel.
          Mobile/tablet: single-column with tab switcher. xl+: side-by-side. */}
      <div className="flex flex-col xl:flex-row gap-3" data-testid="kanban-layout">
        <div className={`flex-1 min-w-0 gap-3 pb-3 flex flex-col sm:flex-row sm:overflow-x-auto -mx-1 px-1 ${mobileTab === "messages" ? "hidden xl:flex" : ""}`} data-testid="kanban-board">
          <Column title="Acil" color={COLS.urgent} icon={Flame} items={buckets.urgent}>
            {buckets.urgent.map((r) => (
              <RequestCard key={r.id} req={r} columnTint={COLS.urgent.tint} isFresh={freshHits.includes(r.id)} {...handlers} />
            ))}
          </Column>
          <Column title="Yeni" color={COLS.newReqs} icon={Inbox} items={buckets.newReqs}>
            {buckets.newReqs.map((r) => (
              <RequestCard key={r.id} req={r} columnTint={COLS.newReqs.tint} isFresh={freshHits.includes(r.id)} {...handlers} />
            ))}
          </Column>
          <Column title="Devam Eden" color={COLS.inProgress} icon={Clock} items={buckets.inProgress}>
            {buckets.inProgress.map((r) => (
              <RequestCard key={r.id} req={r} columnTint={COLS.inProgress.tint} isFresh={freshHits.includes(r.id)} {...handlers} />
            ))}
          </Column>
          <Column title="Tamamlandı" color={COLS.done} icon={CheckCircle2} items={buckets.done}>
            {buckets.done.map((r) => (
              <RequestCard key={r.id} req={r} columnTint={COLS.done.tint} isFresh={false} {...handlers} />
            ))}
          </Column>
        </div>

        {/* Messages side panel — xl: inline fixed. Mobile: full-width if tab=messages. */}
        <aside className={`xl:flex xl:w-80 shrink-0 flex-col ${mobileTab === "messages" ? "flex" : "hidden"}`} data-testid="messages-side-panel">
          <MessagesSidePanel />
        </aside>
      </div>

      {/* Rejected drawer */}
      {buckets.rejected.length > 0 && (
        <div className="mt-5 bg-white border border-stone-200 rounded-xl">
          <button onClick={() => setShowRejected((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-stone-600 hover:bg-stone-50 rounded-xl" data-testid="toggle-rejected">
            {showRejected ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Reddedilen talepler
            <span className="ml-auto text-xs bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5">{buckets.rejected.length}</span>
          </button>
          {showRejected && (
            <div className="p-3 border-t grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {buckets.rejected.map((r) => (
                <RequestCard key={r.id} req={r} columnTint="border-rose-300 opacity-80" isFresh={false} {...handlers} />
              ))}
            </div>
          )}
        </div>
      )}

      {approving && <ApproveModal req={approving} onClose={() => setApproving(null)} onDone={() => { setApproving(null); refresh(); }} />}
      {rejecting && <RejectModal req={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); refresh(); }} />}
      <AppointmentRequestModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingDraft(null); }}
        editingDraft={editingDraft}
        onSaved={refresh}
      />
    </div>
  );
}
